<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" :title="computedTitle" @show="onShow"
                   @hidden="onHidden" :content-in-form="false" modal-class="mobile-full-screen"
                   :show-confirmation="showConfirmation" :is-loading="loading"
                   :hide-footer="disableActions" scrollable>

      <div class="row">
        <div class="col-sm-4 col-xl-3">
          <input-widget :model="model" attribute="lot_no" :disabled="disabled"
                        @keyup.prevent="onLotNumberKeyUp" @keydown="onLotNumberKeyDown" @blur="onLotNumberBlur">
          </input-widget>
        </div>
        <div class="col-sm-4 col-xl-3">
          <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                        :max="$getMonthCurrent(model.created_at)"
                        :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
        </div>
        <div class="col-sm-4 col-xl-3">
          <input-widget
              :model="model" attribute="shipping_address_uuid" type="select2" :items="shippingAddresses"
              display-field="${item.full_address_with_geographic_structure}" :disabled="!(bidder && bidder.customer)"
              :options="{placeholder: $t('Shipping Address')}"
              @select2Select="onShippingAddressSelect" @select2Unselect="calculateData()">
          </input-widget>
        </div>
        <div class="col-sm-4 col-xl-3" v-if="hasMoreThanOneLanguage">
          <language-code-select-input-widget hint :model="model" attribute="preferred_language_code"/>
        </div>
        <div class="col-sm-4 col-xl-3">
          <input-widget :model="model" attribute="complaint_status" type="select2" :items="invoiceComplaintStatuses"
                        value-field="value" display-field="${item.key}"
                        :options="{placeholder: $t('Complaint Status')}"/>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div v-if="bidder">
            <customer-email-address-info :customer="bidder.customer"></customer-email-address-info>
          </div>
        </div>
      </div>

      <b-form @submit.prevent="onSubmit">

        <div class="row mt-4 mb-2">
          <div class="col-md-auto">
            <input-widget @change="calculateData()" is-switch attribute="is_exported"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto">
            <input-widget @change="calculateData()" is-switch
                          attribute="is_registered_business"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto">
            <input-widget @change="calculateData()" is-switch
                          attribute="is_tax_applied"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto" v-if="hasStripeProvider"
               v-permission="'change_customerbankaccount'">
            <input-widget is-switch
                          attribute="exclude_from_auto_payments"
                          :model="model" type="checkbox"/>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-12 d-flex justify-content-between">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">{{ $t('Lots') }}</h5>
            </div>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table table-responsive">
              <tr>
                <th style="width: 100px">{{ $t('Lot') }}</th>
                <th style="width: 180px">{{ $t('Hammer Price') }}</th>
                <th style="width: 180px">{{ $t('Premium') }}</th>
                <th style="width: 190px">{{ $t('Premium VAT') }}</th>
                <th style="width: 190px">{{ $t('Import Tax') }}</th>
              </tr>
              <template v-if="model.invoiced_financial_items.length > 0">
                <tr :key="`invoiced-lot-${lot.uuid}`" v-for="lot in model.invoiced_financial_items">
                  <td>
                    <clickable-lot-no :obj="lot" is-financial-item/>
                  </td>
                  <td>
                    <currency-input-widget :currency="currencySymbol" :model="lot" attribute="hammer_price"
                                           @input="onHammerPriceChange(lot)" :label="false"/>
                  </td>
                  <td class="pr-4">
                    <currency-input-widget :currency="currencySymbol" :model="lot" attribute="premium"
                                           @input="calculateData()" :label="false"/>
                  </td>
                  <td>{{ lot.premium_vat | asCurrency(currencySymbol) }}</td>
                  <td>{{ lot.import_tax | asCurrency(currencySymbol) }}</td>
                </tr>
              </template>
              <tr v-else>
                <td :colspan="5">
                  <no-data-available :height="3"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Shipping') }}</h5>
            <input-widget @change="onShippingCheckboxChange" :model="model" attribute="enable_shipping" type="checkbox"
                          is-switch :label="false" class="ml-3"/>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table">
              <tr>
                <th style="width: 200px">{{ $t('Amount') }}</th>
                <th style="width: 200px">{{ $t('Tracking Code') }}</th>
                <th style="width: 30%">{{ $t('Taxable') }}</th>
                <th></th>
              </tr>
              <tr>
                <td class="pr-4">
                  <currency-input-widget :currency="currencySymbol" @input="calculateData()" :model="model"
                                         attribute="shipping_amount" type="number" :label="false"
                                         :disabled="!model.enable_shipping"/>
                </td>
                <td class="pr-4">
                  <input-widget :disabled="!model.enable_shipping" :model="model" attribute="shipping_tracking_code"
                                :label="false"/>
                </td>
                <td>
                  <input-widget @change="calculateData()" :model="model" attribute="shipping_taxable" type="checkbox"
                                :label="false" :disabled="!model.enable_shipping"/>
                </td>
                <td></td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Comment') }}</h5>
          </div>
          <div class="col-md-12">
            <input-widget :model="model" attribute="comment" type="textarea" label=""/>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-12" v-if="!disableActions">
            <b-button :disabled="!bidder"
                      class="p-0 mb-2" variant="link" @click="onNewMiscellaneousItemClick">
              <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
              {{ $t('Add Miscellaneous Charge') }}
            </b-button>
          </div>
          <div class="col-md-12" v-if="model.miscellaneous_items.length > 0">
            <table class="info-table table table-sm miscellaneous-table">
              <tr>
                <th style="width: 25%">{{ $t('Description') }}</th>
                <th style="width: 25%">{{ $t('Comment') }}</th>
                <th style="width: 20%">{{ $t('Amount') }}</th>
                <th style="width: 20%">{{ $t('Percentage') }}</th>
                <th style="width: 15%" class="text-center">{{ $t('Taxable') }}</th>
                <th style="width: 15%" class="text-center">{{ $t('Actions') }}</th>
              </tr>
              <tr :key="`miscellaneous-item-${index}`" v-for="(item, index) in model.miscellaneous_items">
                <td class="d-flex pr-3">
                  <input-widget v-if="hasPredefinedMiscCharges" style="flex: 1" :items="invoiceMiscellaneousItemCharges"
                                value-field="key"
                                display-field="${item.value}"
                                @select2Select="onMiscChargeDescriptionDropdownValueChange(item)"
                                type="select2" :model="item" :label="false" attribute="title_dropdown_value"
                                :rules="item.amount ? 'required' : ''"/>
                  <input-widget v-if="item.title_dropdown_value === 'other' || !hasPredefinedMiscCharges"
                                style="flex: 1"
                                :class="{'ml-2': hasPredefinedMiscCharges}"
                                :rules="item.amount ? 'required|max:255' : 'max:255'"
                                :model="item" attribute="title" :label="false"/>
                </td>
                <td class="pr-3">
                  <input-widget value-field="key"
                                display-field="${item.value}"
                                type="text" :model="item" :label="false" attribute="comment"/>
                </td>
                <td class="pr-3">
                  <currency-input-widget :currency="currencySymbol" @input="calculateData()"
                                         :rules="item.title ? 'required' : ''" :model="item"
                                         attribute="amount"
                                         :label="false"/>
                </td>
                <td>
                  <percentage-input-widget :model="item" :disabled="!item.inventory_uuid"
                                           @input="onMiscItemPercentageChange(item)" attribute="percentage"
                                           :label="false"/>
                </td>
                <td class="text-center">
                  <input-widget @change="calculateData()" attribute="taxable" :model="item" type="checkbox"
                                :label="false"/>
                </td>
                <td class="text-center">
                  <b-tooltip :target="`remove-commission-button-${index}`" :title="$t('Delete')"/>
                  <font-awesome-icon class="text-danger hover-cursor"
                                     :id="`remove-commission-button-${index}`"
                                     @click="onMiscellaneousItemDelete(index)"
                                     style="font-size: 1.5em;margin-top: .3em"
                                     :icon="'times'"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-5 offset-7">
            <invoice-totals-table :model="model" :financial-items="model.invoiced_financial_items"
                                  :miscellaneous-items="model.miscellaneous_items" :currency="currencySymbol"/>
          </div>
        </div>

      </b-form>

      <template v-slot:modal-footer>
        <div class="d-flex align-items-center mr-auto text-muted" v-if="isRecalculating">
          <b-spinner class="mr-2"/>
          <h4 class="d-inline-block mb-0">
            {{ $t('Calculating') }}
          </h4>
        </div>
        <cancel-button @click="hideContinuousAuctionInvoiceModal" :disabled="loading">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="handleSubmit(onSubmit)" :show-loader="loading"
                       :disabled="!bidder || model.invoiced_financial_items.length === 0">
          {{ $t('Submit') }}
        </submit-button>
      </template>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import CancelButton from "../../core/components/common/CancelButton";
  import SubmitButton from "../../core/components/common/SubmitButton";
  import InvoicesService from "./InvoicesService";
  import {ON_PDF_MODAL_OPEN, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_CLOSE} from "@/plugins/event-bus/event-bus-names";
  import {SOURCE_INVOICES_TABLE} from "../constants";
  import NoDataAvailable from "../../core/components/common/NoDataAvailable";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import _ from 'lodash';
  import MiscellaneousItem from "./models/MiscellaneousItem.model";
  import FinancialItemModel from "@/modules/invoices/models/FinancialItem.model";
  import InvoiceTotalsTable from "@/modules/invoices/InvoiceTotalsTable";
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import LotsService from "../lots/LotsService";
  import ContinuousAuctionInvoiceModel from "./models/ContinuousAuctionInvoice.model";
  import PercentageInputWidget from "../../core/widgets/PercentageInputWidget";
  import CustomerEmailAddressInfo from "../../core/components/customer-info/CustomerEmailAddressInfo";
  import {InvoiceModalMixin} from "@/modules/invoices/modals/invoiceModal.mixin";
  import ClickableLotNo from "@/modules/lots/clickable-lot-no/ClickableLotNo";
  import {hasPermission} from "@/core/directives/permission";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "ContinuousAuctionInvoiceModal",
    mixins: [InvoiceModalMixin],
    components: {
      ClickableLotNo,
      PercentageInputWidget,
      LanguageCodeSelectInputWidget,
      CustomerEmailAddressInfo,
      InvoiceTotalsTable,
      CurrencyInputWidget,
      NoDataAvailable,
      SubmitButton,
      CancelButton,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new ContinuousAuctionInvoiceModel(),
        bidder: null,
        lot: null,
        currentSale: null,
        isRecalculating: false,
        shippingAddresses: [],
        shippingAddressHasHint: false,
        taxRateHasHint: false
      }
    },
    computed: {
      ...globalMapState({
        invoiceMiscellaneousItemCharges: state => state.invoiceMiscellaneousItemCharges,
        hasMoreThanOneLanguage: state => state.hasMoreThanOneLanguage,
        invoiceComplaintStatuses: state => state.invoiceComplaintStatuses,
        isSalesTaxEnabled: state => state.isSalesTaxEnabled,
        hasStripeProvider: state => state.hasStripeProvider,
      }),
      ...mapState({
        modal: state => state.continuousAuctionInvoiceModal,
        tableData: state => state.tableData,
        editObject: state => state.continuousAuctionInvoiceModal.object,
        sale: state => state.continuousAuctionInvoiceModal.sale,
      }),
      computedTitle() {
        if (this.modal.creditNoteWithoutRefundParams) {
          return this.$i18n.t('Recreate Invoice {no}', {no: this.model.invoice_no})
        }
        if (!this.model || !this.model.uuid) {
          return this.$i18n.t('Generate Invoice')
        }
        return this.$i18n.t('Update Invoice {no}', {no: this.model.invoice_no})
      },
      hasPredefinedMiscCharges() {
        return this.invoiceMiscellaneousItemCharges && this.invoiceMiscellaneousItemCharges.length > 1;
      },
      disableActions() {
        return ['cancelled'].includes(this.model.status);
      },
      currencySymbol() {
        if (this.currentSale && this.currentSale.currency) {
          return this.currentSale.currency.symbol;
        }
        return null;
      },
      disabled() {
        return !!this.model.uuid;
      },
      changedLotNo() {
        return this.model.lot_no && (!this.lot || this.lot.lot_no !== this.model.lot_no)
      },
    },
    watch: {
      async editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new ContinuousAuctionInvoiceModel(this.editObject);
          this.currentSale = _.cloneDeep(this.sale);
          this.bidder = this.model.bidder;
          this.model.paddle_number = this.bidder.paddle_number;
          this.model.miscellaneous_items = this.model.miscellaneous_items.map(item => new MiscellaneousItem(item));
          for (let lot of this.bidder.lots) {
            if (this.model.uuid === lot.financial_item.invoice_uuid) {
              this.model.lot_no = lot.lot_no;
              this.model.invoiced_financial_items.push(new FinancialItemModel({
                ...lot.financial_item,
                lot_no: lot.lot_no,
                lot
              }));
            }
          }

          if (this.editObject.bidder && this.editObject.bidder.customer) {
            this.setShippingAddresses(this.editObject.bidder.customer.addresses);

            if (!this.model.preferred_language_code) {
              this.model.preferred_language_code = this.bidder.customer.preferred_language_code
            }
          }
          this.taxRateHasHint = this.calculateTaxRateHasHint();
        }
      }
    },
    methods: {
      ...mapActions(['hideContinuousAuctionInvoiceModal', 'getTableData']),
      async onShow() {
        this.showConfirmation = true;
        this.bidder = null;
        this.loading = false;
      },
      onHidden() {
        this.bidder = null;
        this.currentSale = null;
        this.isRecalculating = false;
        this.lot = null;
        this.model = new ContinuousAuctionInvoiceModel();
        this.hideContinuousAuctionInvoiceModal();
        this.shippingAddresses = [];
        this.shippingAddressHasHint = false;
      },
      onShippingCheckboxChange() {
        this.$nextTick(() => {
          if (!this.model.enable_shipping) {
            this.model.shipping_tracking_code = null;
          }
          this.calculateData();
        })
      },
      async onSubmit() {
        this.model.resetErrors();

        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy invoice'));
          return;
        }

        if (!this.model.paddle_number || !this.model.lot_no) {
          return false;
        }

        let unpaidInvoiceNotificationSuccess = false;
        if (!this.model.uuid || this.modal.creditNoteWithoutRefundParams) {
          unpaidInvoiceNotificationSuccess = await this.$confirm(
              "Would you like to send `New Invoice Notification` to the customer?"
          );
        }

        this.loading = true;
        let response;
        let requestData = {
          uuid: this.model.uuid,
          invoice_tax_rate: this.model.is_tax_rate_set_from_shipping_address ? null : this.model.tax_rate,
          financial_items: this.model.invoiced_financial_items.map(item => {
            if (item.exclude_tax_for_premium === null) {
              item.exclude_tax_for_premium = false;
            }
            if (item.exclude_tax_for_hammer_price === null) {
              item.exclude_tax_for_hammer_price = false;
            }
            return {
              uuid: item.uuid,
              premium: item.premium,
              hammer_price: item.hammer_price,
              exclude_tax_for_premium: item.exclude_tax_for_premium,
              exclude_tax_for_hammer_price: item.exclude_tax_for_hammer_price,
            }
          }),
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value) && item.amount),
          paddle_number: this.model.paddle_number,
          sale_no: this.currentSale.sale_no,
          auction_uuid: this.currentSale.uuid,
          is_exported: this.model.is_exported,
          is_tax_applied: this.model.is_tax_applied,
          is_registered_business: this.model.is_registered_business,
          shipping_amount: this.model.enable_shipping ? this.model.shipping_amount : null,
          shipping_taxable: this.model.enable_shipping ? this.model.shipping_taxable : false,
          shipping_tracking_code: this.model.shipping_tracking_code,
          type: 'auction',
          preferred_language_code: this.model.preferred_language_code,
          complaint_status: this.model.complaint_status || null,
          send_unpaid_invoice_notification: unpaidInvoiceNotificationSuccess,
          shipping_address_uuid: this.model.shipping_address_uuid,
          comment: this.model.comment,
          date: this.model.date
        };

        if (hasPermission('change_customerbankaccount') && this.hasStripeProvider) {
          requestData['exclude_from_auto_payments'] = this.model.exclude_from_auto_payments;
        }

        if (this.modal.creditNoteWithoutRefundParams) {
          requestData.inherited_from_uuid = requestData.uuid;
          requestData.uuid = null;
          requestData.credit_note_without_refund_params = this.modal.creditNoteWithoutRefundParams;
          response = await InvoicesService.createInvoice(requestData);
        } else if (!this.model.uuid) {
          response = await InvoicesService.createInvoice(requestData);
        } else {
          response = await InvoicesService.updateInvoice(this.model.uuid, requestData);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;

          if (this.modal.source === SOURCE_INVOICES_TABLE) {
            this.getTableData();
          }

          if (!this.model.uuid && this.$hasPermission('export_invoice')) {
            const invoice = response.body.invoice;
            this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
              title: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: response.body.invoice.invoice_no}),
            });
            let pdfDataResponse = await InvoicesService.getInvoicePdfData(invoice.uuid);
            if (pdfDataResponse.success) {
              this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
                ...pdfDataResponse.body,
                filename: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no})
              });
            } else {
              let msg = this.$i18n.t("An error occurred during PDF generation");
              if (pdfDataResponse.body && pdfDataResponse.body.non_field_errors) {
                msg = pdfDataResponse.body.non_field_errors
              }
              this.$errorDialog(msg);
              this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
            }
          }
          this.hideContinuousAuctionInvoiceModal();
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.body.miscellaneous_items) {
            for (let index in response.body.miscellaneous_items) {
              this.model.miscellaneous_items[index].setMultipleErrors(response.body.miscellaneous_items[index]);
            }
          } else if (response.body && typeof response.body === 'object') {
            this.model.setMultipleErrors(response.body);
          } else {
            this.$errorDialog(this.$i18n.t("An error occurred during invoice generation."));
          }
        }
      },
      selectedItems(item, selectedValues) {
        let selectedItems = []
        selectedValues.forEach(i => {
          if (i.uuid === item.uuid) {
            selectedItems.push(item.uuid)
          }
        })
        return selectedItems
      },
      calculateData: _.debounce(async function (recalculatePremium = false) {
        const isFormValid = await this.$refs.observer.validate();
        if (!isFormValid) {
          return;
        }

        this.isRecalculating = true;

        const financial_items = [
          ...this.model.invoiced_financial_items.map(item => {
            return {
              uuid: item.uuid,
              premium: recalculatePremium ? null : parseFloat(item.premium),
              hammer_price: parseFloat(item.hammer_price),
              exclude_tax_for_premium: item.exclude_tax_for_premium,
              exclude_tax_for_hammer_price: item.exclude_tax_for_hammer_price,
            }
          }),
        ];

        let expand = [
          'lot.inventory.inventory_miscellaneous_items',
          'invoice_lots',
          'miscellaneous_items',
          'financial_items',
          'buyer_financial_item_charges.charge',
          'unsaved_buyer_financial_item_charges.charge'
        ].join(',')

        let {success, body} = await InvoicesService.calculateData({
          uuid: this.model.uuid,
          invoice_tax_rate: this.model.is_tax_rate_set_from_shipping_address ? null : this.model.tax_rate,
          shipping_address_uuid: this.model.shipping_address_uuid,
          financial_items,
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value) && item.amount),
          sale_no: this.currentSale.sale_no,
          is_exported: this.model.is_exported,
          is_tax_applied: this.model.is_tax_applied,
          is_registered_business: this.model.is_registered_business,
          shipping_amount: this.model.enable_shipping ? this.model.shipping_amount : null,
          shipping_taxable: this.model.enable_shipping ? this.model.shipping_taxable : false,
          type: 'auction'
        }, expand);

        if (success) {
          if (body.financial_items.length > 0) {
            for (let financialItem of body.financial_items) {
              let itemIndex = this.model.invoiced_financial_items.findIndex(i => i.uuid === financialItem.uuid);
              if (itemIndex >= 0) {
                this.model.invoiced_financial_items[itemIndex] = new FinancialItemModel({
                  ...this.model.invoiced_financial_items[itemIndex],
                  ...financialItem
                });
              }
            }
            this.model.invoiced_financial_items = [...this.model.invoiced_financial_items];
          }

          if (body.miscellaneous_items.length > 0) {
            for (let miscItem of body.miscellaneous_items) {
              let itemIndex;
              if (miscItem.uuid) {
                itemIndex = this.model.miscellaneous_items.findIndex(mi => mi.uuid === miscItem.uuid);
              } else {
                itemIndex = this.model.miscellaneous_items.findIndex(mi =>
                    (mi.title === miscItem.title || mi.title_dropdown_value === miscItem.title) && mi.amount == miscItem.amount
                );
              }

              if (itemIndex >= 0) {
                if (miscItem.uuid) {
                  this.model.miscellaneous_items[itemIndex] = new MiscellaneousItem({
                    ...this.model.miscellaneous_items[itemIndex],
                    ...miscItem
                  })
                } else {
                  this.model.miscellaneous_items[itemIndex] = new MiscellaneousItem({
                    ...this.model.miscellaneous_items[itemIndex],
                    amount: miscItem.amount,
                    vat: miscItem.vat,
                    taxable: miscItem.taxable,
                    title: miscItem.title,
                  }, true);
                }
              }
            }
            this.model.miscellaneous_items = [...this.model.miscellaneous_items];
          }

          this.model.tax_rate = body.tax_rate;
          this.model.is_tax_rate_set_from_shipping_address = body.is_tax_rate_set_from_shipping_address;
          this.model.shipping_amount = body.shipping_amount;
          this.model.shipping_vat = body.shipping_vat;
          this.model.shipping_taxable = body.shipping_taxable;

          this.taxRateHasHint = this.calculateTaxRateHasHint(body.shipping_address_tax_data);
        } else {
          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(body);
          }
        }

        this.isRecalculating = false;
      }, 500),
      onHammerPriceChange: _.debounce(async function (financialItem) {
        this.calculateMiscChargesWithPercent(financialItem.lot.inventory_uuid)
        await this.calculateData(true)
      }, 500),
      onMiscItemPercentageChange: _.debounce(async function (miscItem) {
        this.calculateMiscChargesWithPercent(miscItem.inventory_uuid)
        await this.calculateData()
      }, 500),
      calculateTaxRateHasHint: function (shippingAddressTaxData = null) {
        if (!shippingAddressTaxData) {
          shippingAddressTaxData = this.model.shipping_address_tax_data;
        }

        return this.model && this.model.is_tax_rate_set_from_shipping_address && shippingAddressTaxData &&
            shippingAddressTaxData.is_economic_nexus_threshold_reached === false;
      },
      onLotNumberKeyUp($event) {
        if ($event.keyCode === 13 && this.changedLotNo) {
          this.getLotInfo();
        } else if (!this.model.lot_no) {
          this.model.resetErrors('lot_no');
          this.clearLotData();
        }
      },
      onLotNumberKeyDown($event) {
        if ($event.keyCode === 13) {
          $event.preventDefault();
        }
      },
      onLotNumberBlur() {
        if (this.changedLotNo) {
          this.getLotInfo();
        } else if (!this.model.lot_no) {
          this.model.resetErrors('lot_no');
          this.clearLotData();
        }
      },
      clearLotData() {
        this.bidder = null;
        this.lot = null;
        this.currentSale = null;
        this.model = new ContinuousAuctionInvoiceModel();
      },
      getLotInfo: _.debounce(async function () {
        this.model.resetErrors('lot_no');

        let expand = [
          'auction',
          'inventory.inventory_miscellaneous_items',
          'financial_item',
          'bidder.customer.billing_address',
          'bidder.customer.addresses.full_address_with_geographic_structure',
          'bidder.customer.shipping_address,bidder.customer.addresses'
        ]

        if (hasPermission('change_customerbankaccount') && this.hasStripeProvider) {
          expand.push('bidder.customer.term')
        }

        const {success, body} = await LotsService.getLotByLotNo(this.model.lot_no, expand.join(','));

        if (success) {
          let bidderResponse = []

          if (body.length === 0) {
            return
          } else if (body[0] && body[0].financial_item && body[0].financial_item.invoice_uuid) {
            this.model.setErrors('lot_no', [
              this.$i18n.t("Lot {lot_no} has already been invoiced", {lot_no: this.model.lot_no})
            ])
            return
          }

          this.lot = _.cloneDeep(body[0]);
          this.currentSale = this.lot.auction;
          bidderResponse = this.lot.bidder;
          bidderResponse.lots = [_.cloneDeep(this.lot)]

          this.model.miscellaneous_items = [];
          this.model.invoiced_financial_items = [];

          this.bidder = bidderResponse;
          for (let lot of this.bidder.lots) {
            if (lot.financial_item.invoice_uuid === null && lot.status === 'sold') {
              this.model.paddle_number = lot.paddle_number;
              this.model.invoiced_financial_items.push(new FinancialItemModel({
                ...lot.financial_item,
                lot_no: lot.lot_no,
                lot
              }));
            }
          }
          if (this.bidder.customer) {
            const customer = this.bidder.customer;
            if (!this.model.uuid && hasPermission('change_customerbankaccount') && customer.term && this.hasStripeProvider) {
              this.model.exclude_from_auto_payments = customer.term.exclude_from_auto_payments;
            }

            this.model.preferred_language_code = customer.preferred_language_code
            this.shippingAddressHasHint = false;
            this.setShippingAddresses(customer.addresses);
            this.$nextTick(() => {
              if (customer.shipping_address && Object.keys(customer.shipping_address).length) {
                this.model.shipping_address_uuid = customer.shipping_address.uuid;
                this.setTaxTerms(customer.shipping_address.tax_terms);
              }
              this.calculateData();
            })
          }
          this.onAfterMoveToToInvoiced()
        } else {
          this.clearLotData();
          this.shippingAddresses = [];

          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(body);
          }
        }
      }, 500),
      setShippingAddresses(addresses = []) {
        const filteredAddresses = addresses.filter(address => address.type === 'shipping');
        if (filteredAddresses.length) {
          this.shippingAddresses = _.cloneDeep(filteredAddresses);
        } else {
          this.shippingAddresses = [];
          this.model.shipping_address_uuid = null;
          this.calculateData();
          this.shippingAddressHasHint = true;
        }
      },
      onShippingAddressSelect(item) {
        this.setTaxTerms(item.tax_terms);
        this.calculateData();
      },
      setTaxTerms(taxTerms) {
        if (taxTerms && Object.keys(taxTerms).length > 0) {
          this.model.is_tax_rate_set_from_shipping_address = true;
          this.model.invoiced_financial_items.forEach(item => {
            item.exclude_tax_for_premium = taxTerms.exclude_tax_for_premium;
          });
          if (taxTerms.is_economic_nexus_threshold_reached) {
            if (this.model.enable_shipping) {
              this.model.shipping_taxable = true;
            }
            if (this.model.miscellaneous_items.length > 0) {
              this.model.miscellaneous_items.forEach(item => {
                item.taxable = true;
              })
            }
          }
        }
      },
      onAfterMoveToToInvoiced() {
        const inventoryUuids = []
        for (let item of this.model.invoiced_financial_items) {
          if (!this.model.uuid && item.lot.inventory.inventory_miscellaneous_items && item.lot.inventory.inventory_miscellaneous_items.length > 0) {
            for (let miscItem of item.lot.inventory.inventory_miscellaneous_items) {
              this.model.miscellaneous_items.push(new MiscellaneousItem({
                amount: miscItem.amount,
                title: miscItem.title || miscItem.description,
                comment: miscItem.comment,
                taxable: miscItem.taxable,
                percentage: miscItem.percentage,
                inventory_uuid: miscItem.inventory_uuid,
              }, true))
              inventoryUuids.push(miscItem.inventory_uuid)
            }
          }
        }
        for (let uuid of inventoryUuids) {
          this.calculateMiscChargesWithPercent(uuid)
        }
      },
      calculateMiscChargesWithPercent(inventoryUuid) {
        let fi = this.model.invoiced_financial_items.find(fi => fi.lot.inventory_uuid === inventoryUuid)
        if (!fi || !fi.lot.inventory_uuid) {
          return
        }
        for (let i in this.model.miscellaneous_items) {
          let miscItem = this.model.miscellaneous_items[i]
          if (miscItem.inventory_uuid === fi.lot.inventory_uuid && miscItem.percentage !== null && miscItem.percentage) {
            miscItem.amount = parseFloat(parseFloat(fi.hammer_price) * parseFloat((miscItem.percentage / 100))).toFixed(2)
          }
        }

      },
      onMiscChargeDescriptionDropdownValueChange(item) {
        item.title = '';
      },
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .lots-table, .miscellaneous-table {
    /deep/ .form-group {
      margin-top: .2em;
      margin-bottom: .5em;
    }

    tr {
      td {
        vertical-align: middle;
      }
    }
  }
</style>
